/**
 * Box 3 (spec §4.2): the version's weekly outcome as one stack, the baseline at the bottom in the neutral colour (it is
 * not a channel), then each channel's contribution in the bundle's order and colorAt(i), as box 5 draws them. A
 * stopped channel keeps its past weeks (Review Focus 2). A count outcome's title names its units, and its figures print as
 * counts (spec C11).
 *
 * @module components/budget/DecompositionChart
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { colorAt, NEUTRAL_SLICE_COLOR } from '@/components/charts/chartPalette';
import { AXIS_PROPS, CHART_HEIGHT, GRID_PROPS, LEGEND_PROPS, TOOLTIP_PROPS, categoryTick, compactTick } from '@/components/looker/charts/chartTheme';
import { OverviewCard } from '@/components/overview/OverviewBox';
import type { OutcomeMetric } from '@/services/budget/budgetContract';
import type { Bundle } from '@/services/budget/bundleSchema';
import { isCount, outcomeFigure, unitsName } from '@/services/budget/outcomeUnits';
import { platformShortLabel } from '@/utils/platformLabel';

const STACK = 'decomposition';

interface Props {
  weekly: Bundle['weekly'];
  /** The bundle's channels, in its order. */
  channels: Bundle['channels'];
  /** The version's currency (bundle.currency), which a revenue version's figures are in. */
  currency: string;
  /** The version's outcome (bundle.outcome_metric). */
  metric: OutcomeMetric;
}

export const DecompositionChart: React.FC<Props> = ({ weekly, channels, currency, metric }) => {
  const { t } = useTranslation();
  const figure = outcomeFigure(metric, currency);
  const value = (v: unknown): string => figure(typeof v === 'number' ? v : null);
  const title = isCount(metric) ? t('budget.decomposition.titleCount', { units: unitsName(metric, t) }) : t('budget.decomposition.title');
  // A channel id is an AD_PLATFORMS name, so it never collides with week or baseline.
  const rows = weekly.map((w) => ({ week: w.week, baseline: w.baseline, ...w.channels }));
  const layers = [
    { key: 'baseline', name: t('budget.decomposition.baseline'), color: NEUTRAL_SLICE_COLOR },
    ...channels.map((c, i) => ({ key: c.id, name: platformShortLabel(c.id), color: colorAt(i) })),
  ];
  return (
    <OverviewCard id="budget-3" title={title}>
      <p className="-mt-2 mb-2 text-xs text-neutral-700">{t('budget.decomposition.subtitle')}</p>
      <div style={{ height: CHART_HEIGHT }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid {...GRID_PROPS} />
            <XAxis dataKey="week" {...AXIS_PROPS} tickFormatter={categoryTick(t)} />
            <YAxis {...AXIS_PROPS} tickFormatter={compactTick} />
            <Tooltip {...TOOLTIP_PROPS} labelFormatter={categoryTick(t)} formatter={value} />
            <Legend {...LEGEND_PROPS} />
            {layers.map((l) => (
              <Area
                key={l.key} dataKey={l.key} name={l.name} stackId={STACK} stroke={l.color} fill={l.color} fillOpacity={0.85}
                isAnimationActive={false}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <p className="mt-2 text-xs text-neutral-700">{t('budget.decomposition.how')}</p>
    </OverviewCard>
  );
};
